import { useEffect, useMemo, useState } from 'react'
import {
  xShareUrl,
  facebookShareUrl,
  linkedinShareUrl,
  cardImageUrl,
  residentPercentages,
  downloadCardImage,
  nativeShareCard,
} from '../../utils/shareIntent.js'
import { defaultNostrPostText } from '../../utils/nostr.js'

const CARD_FORMATS = [
  { id: 'square', label: 'Square', note: '1080×1080 — Instagram feed, X, LinkedIn' },
  { id: 'story',  label: 'Story',  note: '1080×1920 — Instagram Stories, TikTok, Reels (IG safe-zone)' },
  { id: 'og',     label: 'Preview', note: '1200×630 — link previews (Twitter, Facebook, LinkedIn)' },
]

// Modal share sheet — preview + comment + destination buttons for the
// quick link share, and (UX35) a Civic Receipt image section with its
// own format picker, download / copy-URL / native-share actions.
export function ShareDialog({ item, tally, userVote, isLoggedIn, onPostQuote, onClose }) {
  const [comment, setComment] = useState('')
  const [status, setStatus] = useState(null) // null | { kind: 'ok'|'error', text }
  const [busy, setBusy] = useState(false)
  // UX38: review-and-confirm step before publishing to Nostr. Clicking
  // "Post to Nostr" opens this preview with the final text editable;
  // the user confirms (or edits) before anything hits a relay.
  const [nostrPreview, setNostrPreview] = useState(false)
  const [nostrDraft, setNostrDraft] = useState('')
  // UX35 image share state. Format defaults to square (most-shared).
  // includeVote defaults ON when the viewer has voted — users sharing
  // a poll usually want their stance to be part of the shared card.
  // Uncheck to share pseudonymously.
  const [cardFormat, setCardFormat] = useState('square')
  const [includeVote, setIncludeVote] = useState(() => !!userVote)
  const [imgLoaded, setImgLoaded] = useState(false)
  const [imgError, setImgError] = useState(false)

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const baseUrl = `${window.location.origin}/poll/${item.id}`
  const title = item.title || item.billId || 'a poll on Verdant'
  const trimmed = comment.trim()

  // UX35 followup: pack the current tally (and the user's vote, when
  // the checkbox is on) into shared URLs so the og:image unfurl
  // reflects the moment-in-time snapshot the user is sharing. poll-og
  // reads these params and threads them through to the card URL.
  //
  // UX61 fix: no longer thread the title into the share URL. The Edge
  // pre-render in poll-og now resolves the title from the live Nostr
  // event (with polls-meta.json as the fallback), so `t=<title>` on
  // the share URL added 150-400 chars of URL-encoded dead weight —
  // Twitter + LinkedIn previews read as ugly URL blobs. Dropping it
  // keeps the og:image render deterministic because poll-og still
  // passes the fetched title to poll-card internally.
  const resident = useMemo(() => residentPercentages(tally), [tally])
  const url = (ref) => {
    const u = new URL(baseUrl)
    u.searchParams.set('ref', ref)
    u.searchParams.set('y', String(resident.yes))
    u.searchParams.set('n', String(resident.no))
    u.searchParams.set('u', String(resident.unsure))
    if (includeVote && userVote) {
      u.searchParams.set('v', String(userVote).toLowerCase())
    }
    return u.toString()
  }

  const copy = async () => {
    try {
      const text = trimmed ? `${trimmed}\n${url('copy')}` : url('copy')
      await navigator.clipboard.writeText(text)
      setStatus({ kind: 'ok', text: 'Link copied to clipboard.' })
    } catch {
      setStatus({ kind: 'error', text: 'Copy failed. Select and copy manually.' })
    }
  }

  const shareX = () => {
    window.open(
      xShareUrl({ text: trimmed || `${title} — vote on Verdant`, url: url('x') }),
      '_blank', 'noopener,noreferrer'
    )
    setStatus({ kind: 'ok', text: 'Opened X in a new tab.' })
  }

  const shareFacebook = () => {
    window.open(facebookShareUrl({ url: url('fb') }), '_blank', 'noopener,noreferrer')
    setStatus({ kind: 'ok', text: 'Opened Facebook in a new tab.' })
  }

  const shareLinkedIn = () => {
    window.open(linkedinShareUrl({ url: url('li') }), '_blank', 'noopener,noreferrer')
    setStatus({ kind: 'ok', text: 'Opened LinkedIn in a new tab.' })
  }

  // UX35 image share helpers.
  const imgUrl = useMemo(() => cardImageUrl({
    id: item.id,
    format: cardFormat,
    resident,
    includeVote,
    userVote,
    title,
  }), [item.id, cardFormat, resident, includeVote, userVote, title])

  // Reset load state whenever the image URL changes (format or vote
  // toggle). The <img> onLoad / onError flip these when the fresh
  // request resolves.
  useEffect(() => {
    setImgLoaded(false)
    setImgError(false)
  }, [imgUrl])

  const filename = `verdant-poll-${(item.id || '').slice(0, 8)}-${cardFormat}.png`

  const downloadImg = async () => {
    setStatus(null)
    const ok = await downloadCardImage(imgUrl, filename)
    setStatus(ok
      ? { kind: 'ok', text: 'Image downloaded.' }
      : { kind: 'error', text: 'Download failed. Try again.' })
  }

  const copyImgUrl = async () => {
    try {
      await navigator.clipboard.writeText(imgUrl)
      setStatus({ kind: 'ok', text: 'Image URL copied.' })
    } catch {
      setStatus({ kind: 'error', text: 'Copy failed. Select and copy manually.' })
    }
  }

  const shareImgNative = async () => {
    setStatus(null)
    const pageUrl = url('native')
    const result = await nativeShareCard({
      url: imgUrl,
      filename,
      text: trimmed || `${title} — vote on Verdant`,
      pageUrl,
    })
    if (result === 'shared') {
      setStatus({ kind: 'ok', text: 'Shared.' })
    } else if (result === 'canceled') {
      // No status — user canceled is a normal path, not an error.
    } else if (result === 'unsupported') {
      // Desktop browsers without Web Share: silently copy the page URL
      // so the user has an immediate next step instead of a dead button.
      try {
        await navigator.clipboard.writeText(pageUrl)
        setStatus({ kind: 'ok', text: 'Share not available here — link copied to clipboard.' })
      } catch {
        setStatus({ kind: 'error', text: 'Share not supported in this browser. Use Download or Copy image URL.' })
      }
    } else {
      // Native share surfaced an error (not a cancel). Fall back to
      // clipboard with a plain-language message instead of a red error.
      try {
        await navigator.clipboard.writeText(pageUrl)
        setStatus({ kind: 'ok', text: 'Share failed — link copied to clipboard instead.' })
      } catch {
        setStatus({ kind: 'error', text: 'Share failed. Try Download or Copy image URL.' })
      }
    }
  }

  const canNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  // UX38: clicking "Post to Nostr" opens the review step rather than
  // publishing. The user sees the final post content, can edit anywhere
  // in it, then clicks "Post" from the review view to actually publish.
  const openNostrPreview = () => {
    if (busy) return
    if (!isLoggedIn) {
      // Close so the auth modal that onPostQuote opens isn't covered.
      onClose()
      onPostQuote?.({ item, title, shareUrl: url('nostr'), comment: trimmed })
      return
    }
    setNostrDraft(defaultNostrPostText({
      title,
      shareUrl: url('nostr'),
      comment: trimmed,
    }))
    setStatus(null)
    setNostrPreview(true)
  }

  const confirmNostrPost = async () => {
    if (busy) return
    if (!nostrDraft.trim()) {
      setStatus({ kind: 'error', text: 'Post text is empty.' })
      return
    }
    setBusy(true)
    setStatus(null)
    try {
      const ok = await onPostQuote?.({
        item,
        title,
        shareUrl: url('nostr'),
        content: nostrDraft,
      })
      if (ok) {
        setStatus({ kind: 'ok', text: 'Posted to Nostr — closing…' })
        setTimeout(() => onClose(), 1500)
      } else {
        setStatus({ kind: 'error', text: 'Failed to post. Try again.' })
      }
    } finally {
      setBusy(false)
    }
  }

  const cancelNostrPreview = () => {
    setNostrPreview(false)
    setStatus(null)
  }

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-title"
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <h2 id="share-title">
            {nostrPreview ? 'Review your Nostr post' : 'Share poll'}
          </h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >×</button>
        </header>

        {nostrPreview ? (
          <div className="modal-body">
            <p className="card-summary" style={{ marginBottom: 12 }}>
              This is what will post to Nostr. Edit it however you like, then
              click Post to publish.
            </p>
            <div className="field">
              <label htmlFor="nostr-draft">Post text</label>
              <textarea
                id="nostr-draft"
                rows={8}
                value={nostrDraft}
                onChange={(e) => setNostrDraft(e.target.value)}
                autoFocus
              />
            </div>
            <div className="share-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={cancelNostrPreview}
                disabled={busy}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmNostrPost}
                disabled={busy || !nostrDraft.trim()}
              >
                {busy ? 'Posting…' : 'Post to Nostr'}
              </button>
            </div>
            {status && (
              <div
                className={status.kind === 'error' ? 'error-state' : 'note'}
                style={{ marginTop: 12 }}
              >
                {status.text}
              </div>
            )}
          </div>
        ) : (
        <div className="modal-body">
          <div className="share-preview">
            <div className="share-preview-meta">
              {item.levelLabel && (
                <span className={`badge ${item.kind === 'candidate' ? 'candidate' : item.level || ''}`}>
                  {item.levelLabel}
                </span>
              )}
              {item.billId && <span className="bill-id">{item.billId}</span>}
            </div>
            <div className="share-preview-title">{title}</div>
            <div className="share-preview-url">{baseUrl}</div>
          </div>

          <div className="field" style={{ marginTop: 14, marginBottom: 0 }}>
            <label htmlFor="share-comment">Add a note (optional)</label>
            <textarea
              id="share-comment"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What do you want to say about this poll?"
            />
          </div>

          <div className="share-actions">
            <button type="button" className="btn btn-secondary" onClick={copy}>
              Copy link
            </button>
            <button type="button" className="btn btn-secondary" onClick={shareX}>
              Share on X
            </button>
            <button type="button" className="btn btn-secondary" onClick={shareFacebook}>
              Share on Facebook
            </button>
            <button type="button" className="btn btn-secondary" onClick={shareLinkedIn}>
              Share on LinkedIn
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={openNostrPreview}
              aria-disabled={busy || undefined}
              title={isLoggedIn ? '' : 'Sign in to post on Nostr'}
            >
              Post to Nostr…
            </button>
          </div>

          {/* UX35: Civic Receipt image section */}
          <div className="share-card-section">
            <h3 className="share-card-head">Share as image</h3>
            <p className="share-card-sub">
              Download a tally card you can post on Instagram, TikTok, or anywhere else.
            </p>
            <div className="share-card-formats" role="tablist" aria-label="Image format">
              {CARD_FORMATS.map((fmt) => (
                <button
                  key={fmt.id}
                  type="button"
                  role="tab"
                  aria-selected={cardFormat === fmt.id}
                  className={`share-card-format ${cardFormat === fmt.id ? 'active' : ''}`}
                  onClick={() => setCardFormat(fmt.id)}
                  title={fmt.note}
                >
                  {fmt.label}
                </button>
              ))}
            </div>
            <div className={`share-card-preview share-card-preview-${cardFormat}`}>
              {!imgError ? (
                <img
                  src={imgUrl}
                  alt="Share card preview"
                  onLoad={() => setImgLoaded(true)}
                  onError={() => setImgError(true)}
                  style={{ opacity: imgLoaded ? 1 : 0 }}
                />
              ) : (
                <div className="share-card-preview-error">
                  Preview unavailable. Download still works.
                </div>
              )}
              {!imgLoaded && !imgError && (
                <div className="share-card-preview-loading">Generating…</div>
              )}
            </div>
            {userVote && (
              <label className="checkbox-row" style={{ marginTop: 10 }}>
                <input
                  type="checkbox"
                  checked={includeVote}
                  onChange={(e) => setIncludeVote(e.target.checked)}
                />
                Include &ldquo;I voted {userVote}&rdquo; on the card
              </label>
            )}
            <div className="share-actions" style={{ marginTop: 10 }}>
              <button type="button" className="btn btn-primary" onClick={downloadImg}>
                Download image
              </button>
              <button type="button" className="btn btn-secondary" onClick={copyImgUrl}>
                Copy image URL
              </button>
              {canNativeShare && (
                <button type="button" className="btn btn-secondary" onClick={shareImgNative}>
                  Share…
                </button>
              )}
            </div>
          </div>

          {status && (
            <div
              className={status.kind === 'error' ? 'error-state' : 'note'}
              style={{ marginTop: 12 }}
            >
              {status.text}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  )
}
